import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { Location, PractitionerRole, Schedule } from '@medplum/fhirtypes';
import { BARIENDO_CT_SYSTEM, ScheduleCategory } from '@bariendo/lib/constants';
import dayjs from 'dayjs';
import { buildTimeBlockRows, TimeBlockRow } from './timeBlocks.utils';

export interface UseTimeBlockSlotsOptions {
  startDate: Date;
  endDate: Date;
}

export interface UseTimeBlockSlotsResult {
  rows: TimeBlockRow[];
  isLoading: boolean;
  error: Error | undefined;
  refresh: () => void;
}

/**
 * Loads `free` procedure-availability Slots in a date range and flattens them into
 * table rows, resolving provider (via Schedule -> PractitionerRole) and location.
 */
export function useTimeBlockSlots({ startDate, endDate }: UseTimeBlockSlotsOptions): UseTimeBlockSlotsResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<TimeBlockRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const startIso = dayjs(startDate).startOf('day').toISOString();
  const endIso = dayjs(endDate).endOf('day').toISOString();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(undefined);

    async function load(): Promise<void> {
      const [slots, schedules, practitionerRoles, locations] = await Promise.all([
        medplum.searchResources('Slot', [
          ['status', 'free'],
          ['service-category', `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.APPOINTMENT_AVAILABILITY}`],
          ['start', `ge${startIso}`],
          ['start', `le${endIso}`],
          ['_sort', 'start'],
          ['_count', '1000'],
        ]),
        medplum.searchResources('Schedule', {
          'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.APPOINTMENT_AVAILABILITY}`,
          _count: '1000',
        }) as Promise<Schedule[]>,
        medplum.searchResources('PractitionerRole', {
          _count: '1000',
          'active:not': false,
        }) as Promise<PractitionerRole[]>,
        medplum.searchResources('Location', { _count: '1000' }) as Promise<Location[]>,
      ]);

      if (cancelled) {
        return;
      }
      setRows(buildTimeBlockRows([...slots], { schedules, practitionerRoles, locations }));
    }

    load()
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, startIso, endIso, refreshKey]);

  return { rows, isLoading, error, refresh };
}
